import React, { useState, useEffect } from 'react';
import { ApiClient } from '../../api/client';
import type { AdminUser, Tenant } from '../../types';
import { useAuth } from '../../context/AuthContext';
import {
  UserCog,
  Plus,
  Mail,
  CheckCircle,
  XCircle,
  Search,
  RefreshCw,
  Edit2,
  Trash2,
  Shield,
  ShieldAlert,
  ShieldOff,
  Building2,
  Lock,
  Bell,
  Ban,
  Check,
  X,
  AlertTriangle,
  AlertCircle,
  Sparkles,
  Eye,
  EyeOff
} from 'lucide-react';

export const UserAdminView: React.FC = () => {
  const { user: currentUser, role: currentUserRole, tenant } = useAuth();
  const isSuper = currentUserRole === 'superadmin' || currentUserRole === 'tenant_admin' || currentUser?.granular_role === 'superadmin' || currentUser?.granular_role === 'tenant_admin';
  const isOrg = !isSuper && (currentUserRole === 'org_admin' || currentUser?.granular_role === 'org_admin');
  const canManageUsers = isSuper || isOrg;

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedTenantFilter, setSelectedTenantFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'disabled'>('all');
  const [demoFilter, setDemoFilter] = useState<'all' | 'demo' | 'hidden'>('all');

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [deletingUser, setDeletingUser] = useState<AdminUser | null>(null);

  // Form states
  const [createForm, setCreateForm] = useState({
    name: '',
    email: '',
    notification_email: '',
    password: '',
    role: 'operator',
    tenant_id: currentUser?.tenant_id || 1,
    show_in_demo: 0,
  });

  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    notification_email: '',
    role: 'operator',
    active: 1,
    password: '',
    tenant_id: 1,
    show_in_demo: 0,
  });

  const [actionLoading, setActionLoading] = useState(false);
  const [actionMsg, setActionMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showCreatePassword, setShowCreatePassword] = useState(false);
  const [showEditPassword, setShowEditPassword] = useState(false);

  const loadUsers = async () => {
    setLoading(true);
    const tenantIdParam = isSuper && selectedTenantFilter !== 'all' ? parseInt(selectedTenantFilter) : undefined;
    const res = await ApiClient.getUsers(tenantIdParam);
    if (res.ok && res.data) {
      setUsers(res.data);
    }
    setLoading(false);
  };

  const loadTenants = async () => {
    if (isSuper) {
      const res = await ApiClient.getTenants();
      if (res.ok && res.data) {
        setTenants(res.data);
      }
    }
  };

  useEffect(() => {
    if (!canManageUsers) return;
    loadUsers();
    loadTenants();
  }, [selectedTenantFilter, canManageUsers]);

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!createForm.email || !createForm.name || !createForm.password) return;

    setActionLoading(true);
    setActionMsg(null);

    const res = await ApiClient.createUser({
      name: createForm.name.trim(),
      email: createForm.email.trim(),
      notification_email: createForm.notification_email.trim() || undefined,
      password: createForm.password,
      role: createForm.role,
      tenant_id: isSuper ? Number(createForm.tenant_id) : undefined,
      show_in_demo: createForm.show_in_demo,
    });

    if (res.ok) {
      setShowCreateModal(false);
      setCreateForm({
        name: '',
        email: '',
        notification_email: '',
        password: '',
        role: 'operator',
        tenant_id: currentUser?.tenant_id || 1,
        show_in_demo: 0,
      });
      loadUsers();
    } else {
      setActionMsg({ type: 'error', text: res.error || 'Hiba a felhasználó létrehozásakor' });
    }
    setActionLoading(false);
  };

  const openEditModal = (u: AdminUser) => {
    setEditingUser(u);
    setEditForm({
      name: u.name,
      email: u.email,
      notification_email: u.notification_email || '',
      role: u.granular_role || u.role,
      active: u.active,
      password: '',
      tenant_id: u.tenant_id,
      show_in_demo: u.show_in_demo ?? 0,
    });
    setActionMsg(null);
    setShowEditModal(true);
  };

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;

    setActionLoading(true);
    setActionMsg(null);

    const res = await ApiClient.updateUser(editingUser.id, {
      name: editForm.name.trim(),
      email: editForm.email.trim(),
      notification_email: editForm.notification_email.trim() || undefined,
      role: editForm.role,
      active: editForm.active,
      password: editForm.password ? editForm.password : undefined,
      tenant_id: isSuper ? Number(editForm.tenant_id) : undefined,
      show_in_demo: editForm.show_in_demo,
    });

    if (res.ok) {
      setShowEditModal(false);
      setEditingUser(null);
      loadUsers();
    } else {
      setActionMsg({ type: 'error', text: res.error || 'Hiba a felhasználó módosításakor' });
    }
    setActionLoading(false);
  };

  const handleToggleActive = async (u: AdminUser) => {
    if (u.id === currentUser?.id) {
      alert('Saját fiókját nem tilthatja le!');
      return;
    }

    const res = await ApiClient.toggleUserActive(u.id);
    if (res.ok) {
      loadUsers();
    } else {
      alert(res.error || 'Hiba a státusz módosításakor');
    }
  };

  const handleToggleDemo = async (u: AdminUser) => {
    setActionLoading(true);
    const res = await ApiClient.toggleUserDemo(u.id);
    if (res.ok) {
      loadUsers();
    } else {
      alert(res.error || 'Hiba a bejelentkező oldali láthatóság módosításakor');
    }
    setActionLoading(false);
  };

  const handleDeleteUser = async () => {
    if (!deletingUser) return;
    if (deletingUser.id === currentUser?.id) {
      alert('Saját fiókját nem törölheti!');
      setDeletingUser(null);
      return;
    }

    setActionLoading(true);
    const res = await ApiClient.deleteUser(deletingUser.id);
    if (res.ok) {
      setDeletingUser(null);
      loadUsers();
    } else {
      alert(res.error || 'Hiba a felhasználó törlése során');
    }
    setActionLoading(false);
  };

  const handleAdminReset2FA = async (u: AdminUser) => {
    const ok = window.confirm(
      `Biztosan kikapcsolja a kétlépcsős azonosítást (2FA) a(z) ${u.name} (${u.email}) felhasználó fiókjából?\n\nHasználja ezt a funkciót például akkor, ha a felhasználó elhagyta a mobiltelefonját. A feloldás után a felhasználó sima jelszóval közvetlenül be tud majd lépni, és szükség esetén új 2FA eszközt tud beállítani.`
    );
    if (!ok) return;

    setActionLoading(true);
    const res = await ApiClient.resetUser2FA(u.id);
    if (res.ok) {
      alert(res.message || 'A 2FA védelem sikeresen kikapcsolva a felhasználó fiókjában.');
      loadUsers();
    } else {
      alert(res.error || 'Hiba a 2FA kikapcsolásakor');
    }
    setActionLoading(false);
  };

  const getRoleBadge = (userRole: string) => {
    switch (userRole) {
      case 'superadmin':
      case 'tenant_admin':
        return <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-500/30">Superadmin</span>;
      case 'org_admin':
        return <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border border-indigo-500/30">Szervezeti Admin</span>;
      case 'editor':
        return <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/30">Szerkesztő</span>;
      case 'operator':
        return <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30">Ügyintéző</span>;
      case 'auditor':
        return <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-500/10 text-purple-600 dark:text-purple-400 border border-purple-500/30">Auditor (Csak olvasás)</span>;
      default:
        return <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">Felhasználó</span>;
    }
  };

  const demoCount = users.filter((u) => u.show_in_demo === 1).length;
  const hiddenCount = users.filter((u) => (u.show_in_demo ?? 0) === 0).length;

  const filtered = users.filter((u) => {
    const matchesSearch =
      u.name.toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase()) ||
      (u.notification_email && u.notification_email.toLowerCase().includes(search.toLowerCase())) ||
      u.role.toLowerCase().includes(search.toLowerCase()) ||
      (u.tenant_name && u.tenant_name.toLowerCase().includes(search.toLowerCase()));

    const matchesStatus =
      statusFilter === 'all'
        ? true
        : statusFilter === 'active'
        ? u.active === 1
        : u.active === 0;

    const matchesDemo =
      demoFilter === 'all'
        ? true
        : demoFilter === 'demo'
        ? u.show_in_demo === 1
        : (u.show_in_demo ?? 0) === 0;

    return matchesSearch && matchesStatus && matchesDemo;
  });

  if (!canManageUsers) {
    return (
      <div className="p-8 text-center bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl max-w-xl mx-auto space-y-4 shadow-xs mt-12">
        <ShieldAlert className="w-12 h-12 text-rose-600 dark:text-rose-400 mx-auto" />
        <h2 className="text-xl font-bold text-slate-900 dark:text-white">Hozzáférés Megtagadva</h2>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          A felhasználók és jogosultságok kezeléséhez adminisztrátori (szervezeti adminisztrátor vagy rendszergazda) jogosultság szükséges. Az Ön fiókja nem rendelkezik ezzel a jogosultsággal.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-5 border-b border-slate-200 dark:border-slate-800">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2.5">
            <UserCog className="w-7 h-7 text-indigo-600 dark:text-indigo-400" />
            <span>Felhasználók & Jogosultságok (RBAC)</span>
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {isSuper
              ? 'Minden intézmény és belső felhasználó központi felügyelete, 2FA és jogosultsági mátrix'
              : 'Szervezeti felhasználók, jogosultságok, értesítési e-mailek és fiókstátusz kezelése'}
          </p>
          <div className="flex flex-wrap items-center gap-2 mt-2.5">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-xl bg-slate-100 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 text-xs text-slate-700 dark:text-slate-200 font-medium shadow-2xs">
              <Building2 className="w-3.5 h-3.5 text-indigo-600 dark:text-cyan-400 shrink-0" />
              <span>Szervezet: <strong className="font-semibold text-slate-900 dark:text-white">{tenant?.name || (currentUser as any)?.tenant_name || `Szervezet #${currentUser?.tenant_id}`}</strong></span>
            </div>
            {tenant?.slug && (
              <span className="text-[11px] font-mono text-slate-400 dark:text-slate-500">
                (/{tenant.slug})
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={loadUsers}
            className="p-2.5 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-xl transition-all cursor-pointer shadow-xs"
            title="Frissítés"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => {
              setActionMsg(null);
              setShowCreateModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white font-medium rounded-xl text-sm shadow-md shadow-indigo-600/20 transition-all cursor-pointer"
          >
            <Plus className="w-4 h-4" />
            <span>Új Felhasználó</span>
          </button>
        </div>
      </div>

      {/* Information Banner regarding Login Demo Accounts */}
      <div className="p-4 rounded-2xl bg-gradient-to-r from-amber-500/10 via-indigo-500/10 to-slate-500/5 border border-amber-500/20 dark:border-amber-400/20 shadow-xs">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5">
            <Sparkles className="w-5 h-5" />
          </div>
          <div className="space-y-1">
            <h4 className="text-sm font-semibold text-slate-900 dark:text-white flex items-center gap-2">
              <span>Bejelentkezési Tesztfiókok Szabályozása</span>
              <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-amber-100 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 border border-amber-300/40">
                {demoCount} tesztfiók a kezdőlapon
              </span>
            </h4>
            <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
              Itt szabályozhatja, hogy a bejelentkező oldalon mely felhasználók jelenjenek meg <strong>1-kattintásos tesztbelépési gombként</strong>.
              Ez <strong>teljesen független a fiók tiltásától és engedélyezésétől</strong>: éles üzemmódban, ha egyetlen fiók sincs tesztként bejelölve (0 db), a kezdőlapon egyáltalán nem jelenik meg a tesztlista, miközben minden regisztrált és aktív felhasználó normál e-mail/jelszó párossal zavartalanul beléphet.
            </p>
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Keresés név, e-mail vagy szervezet szerint..."
            className="w-full pl-10 pr-4 py-2 bg-white dark:bg-slate-900/80 border border-slate-300 dark:border-slate-800 rounded-xl text-slate-900 dark:text-slate-200 text-sm placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-xs"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Tenant filter (Superadmin only) */}
          {isSuper && tenants.length > 0 && (
            <div className="flex items-center gap-1.5 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-800 rounded-xl px-2.5 py-1.5 shadow-xs">
              <Building2 className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <select
                value={selectedTenantFilter}
                onChange={(e) => setSelectedTenantFilter(e.target.value)}
                className="text-xs bg-transparent text-slate-700 dark:text-slate-200 border-none focus:outline-none cursor-pointer pr-2"
              >
                <option value="all">Minden Szervezet ({tenants.length})</option>
                {tenants.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Status filter */}
          <div className="flex items-center bg-slate-100 dark:bg-slate-900/80 p-1 rounded-xl border border-slate-200 dark:border-slate-800 text-xs">
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-3 py-1 rounded-lg font-medium transition ${
                statusFilter === 'all'
                  ? 'bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-xs'
                  : 'text-slate-500 hover:text-slate-900 dark:hover:text-slate-300'
              }`}
            >
              Összes
            </button>
            <button
              onClick={() => setStatusFilter('active')}
              className={`px-3 py-1 rounded-lg font-medium transition ${
                statusFilter === 'active'
                  ? 'bg-emerald-600 text-white shadow-xs'
                  : 'text-slate-500 hover:text-slate-900 dark:hover:text-slate-300'
              }`}
            >
              Aktívak
            </button>
            <button
              onClick={() => setStatusFilter('disabled')}
              className={`px-3 py-1 rounded-lg font-medium transition ${
                statusFilter === 'disabled'
                  ? 'bg-rose-600 text-white shadow-xs'
                  : 'text-slate-500 hover:text-slate-900 dark:hover:text-slate-300'
              }`}
            >
              Felfüggesztettek
            </button>
          </div>

          {/* Demo users filter */}
          <div className="flex items-center bg-slate-100 dark:bg-slate-900/80 p-1 rounded-xl border border-slate-200 dark:border-slate-800 text-xs">
            <button
              onClick={() => setDemoFilter('all')}
              className={`px-3 py-1 rounded-lg font-medium transition ${
                demoFilter === 'all'
                  ? 'bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-xs'
                  : 'text-slate-500 hover:text-slate-900 dark:hover:text-slate-300'
              }`}
            >
              Mind
            </button>
            <button
              onClick={() => setDemoFilter('demo')}
              className={`px-3 py-1 rounded-lg font-medium transition flex items-center gap-1.5 ${
                demoFilter === 'demo'
                  ? 'bg-amber-500 text-slate-950 font-bold shadow-xs'
                  : 'text-amber-600 dark:text-amber-400 hover:text-amber-700'
              }`}
              title="Csak a bejelentkező oldalon megjelenő tesztfiókok"
            >
              <Sparkles className="w-3.5 h-3.5" />
              <span>Teszt ({demoCount})</span>
            </button>
            <button
              onClick={() => setDemoFilter('hidden')}
              className={`px-3 py-1 rounded-lg font-medium transition ${
                demoFilter === 'hidden'
                  ? 'bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-xs'
                  : 'text-slate-500 hover:text-slate-900 dark:hover:text-slate-300'
              }`}
              title="Normál fiókok (nem jelennek meg tesztgombként a kezdőlapon)"
            >
              Rejtett ({hiddenCount})
            </button>
          </div>

          <div className="text-xs text-slate-500 dark:text-slate-400 pl-2">
            Találat: <span className="text-slate-900 dark:text-white font-medium">{filtered.length}</span>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden shadow-xs dark:shadow-xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 dark:bg-slate-950/70 border-b border-slate-200 dark:border-slate-800 text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              <tr>
                <th className="px-3 sm:px-5 py-3 sm:py-3.5 font-semibold whitespace-nowrap">Név / E-mail</th>
                <th className="px-3 sm:px-4 py-3 sm:py-3.5 font-semibold whitespace-nowrap">Szervezet</th>
                <th className="px-3 sm:px-4 py-3 sm:py-3.5 font-semibold whitespace-nowrap">Szerepkör</th>
                <th className="px-3 sm:px-4 py-3 sm:py-3.5 font-semibold whitespace-nowrap">2FA Védelem</th>
                <th className="px-3 sm:px-4 py-3 sm:py-3.5 font-semibold whitespace-nowrap">Fiók Státusz</th>
                <th className="px-3 sm:px-4 py-3 sm:py-3.5 font-semibold whitespace-nowrap">Kezdőlapi Teszt</th>
                <th className="px-3 sm:px-5 py-3 sm:py-3.5 font-semibold text-right whitespace-nowrap sticky right-0 bg-slate-50 dark:bg-slate-950 z-10 shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.06)]">Műveletek</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-800/60">
              {loading ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-slate-500">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto text-indigo-500 mb-2" />
                    Felhasználók betöltése...
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-slate-500">
                    Nincs a szűrésnek megfelelő felhasználó.
                  </td>
                </tr>
              ) : (
                filtered.map((u) => {
                  const isCurrent = u.id === currentUser?.id;
                  return (
                    <tr
                      key={u.id}
                      className={`hover:bg-slate-50/70 dark:hover:bg-slate-800/40 transition-colors ${
                        !u.active ? 'bg-rose-500/[0.02] dark:bg-rose-950/[0.05]' : ''
                      }`}
                    >
                      {/* Name & Email & Notification Email */}
                      <td className="px-3 sm:px-5 py-3.5">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-900 dark:text-white">{u.name}</span>
                          {isCurrent && (
                            <span className="px-1.5 py-0.2 rounded text-[10px] font-bold bg-indigo-100 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800">
                              Ön
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1.5 mt-0.5">
                          <Mail className="w-3 h-3 text-slate-400 shrink-0" />
                          <span className="truncate max-w-[200px]">{u.email}</span>
                        </div>
                        {u.notification_email && (
                          <div
                            className="text-[11px] text-indigo-600 dark:text-indigo-400 flex items-center gap-1 mt-0.5"
                            title="Értesítési e-mail cím"
                          >
                            <Bell className="w-3 h-3 text-indigo-400 shrink-0" />
                            <span className="truncate max-w-[200px]">{u.notification_email}</span>
                          </div>
                        )}
                      </td>

                      {/* Organization */}
                      <td className="px-3 sm:px-4 py-3.5">
                        <div className="flex items-center gap-1.5 text-xs text-slate-700 dark:text-slate-300 font-medium">
                          <Building2 className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                          <span className="truncate max-w-[150px] sm:max-w-[180px]">{u.tenant_name || tenant?.name || `Szervezet #${u.tenant_id}`}</span>
                        </div>
                      </td>

                      {/* Role */}
                      <td className="px-3 sm:px-4 py-3.5 whitespace-nowrap">
                        {getRoleBadge(u.granular_role || u.role)}
                      </td>

                      {/* 2FA Status */}
                      <td className="px-3 sm:px-4 py-3.5 whitespace-nowrap">
                        {u.totp_enabled ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800/60">
                            <Shield className="w-3 h-3 text-emerald-500" />
                            <span>2FA Védett</span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-slate-400 dark:text-slate-500 font-normal">
                            <ShieldAlert className="w-3.5 h-3.5 text-slate-300 dark:text-slate-600" />
                            <span>Nincs 2FA</span>
                          </span>
                        )}
                      </td>

                      {/* Active / Suspended Status */}
                      <td className="px-3 sm:px-4 py-3.5 whitespace-nowrap">
                        {u.active ? (
                          <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400 font-medium">
                            <CheckCircle className="w-3.5 h-3.5" />
                            <span>Aktív</span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-rose-600 dark:text-rose-400 font-medium">
                            <XCircle className="w-3.5 h-3.5" />
                            <span>Felfüggesztve</span>
                          </span>
                        )}
                      </td>

                      {/* Login Demo Account Toggle */}
                      <td className="px-3 sm:px-4 py-3.5 whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => handleToggleDemo(u)}
                          disabled={actionLoading}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl text-xs font-semibold transition cursor-pointer border ${
                            u.show_in_demo === 1
                              ? 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30 hover:bg-amber-500/20'
                              : 'bg-slate-100 dark:bg-slate-800/80 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-700/60 hover:bg-slate-200/80 dark:hover:bg-slate-700'
                          }`}
                          title={
                            (u.tenant_id === 1
                              ? 'Rendszerszervezet: a KÖZPONTI belépési oldalon jelenik meg (csak superadmin kapcsolhatja). '
                              : 'Csak a szervezet SAJÁT, egyedi belépési oldalán jelenik meg (Szervezetek → Belépési link). ') +
                            (u.show_in_demo === 1
                              ? 'Jelenleg megjelenik 1-kattintásos belépési gombként. Kattintson az elrejtéshez.'
                              : 'Jelenleg rejtve (normál e-mail/jelszavas belépés). Kattintson a megjelenítéshez.')
                          }
                        >
                          {u.show_in_demo === 1 ? (
                            <>
                              <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                              <span>{u.tenant_id === 1 ? 'Központi oldalon' : 'Belépési oldalon'}</span>
                            </>
                          ) : (
                            <>
                              <EyeOff className="w-3.5 h-3.5 text-slate-400" />
                              <span>Rejtve</span>
                            </>
                          )}
                        </button>
                      </td>

                      {/* Actions */}
                      <td className="px-3 sm:px-5 py-3.5 text-right whitespace-nowrap sticky right-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur-2xs z-10 shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.06)]">
                        <div className="flex items-center justify-end gap-1.5">
                          {/* Toggle Active (Tiltás / Engedélyezés) */}
                          <button
                            onClick={() => handleToggleActive(u)}
                            disabled={isCurrent}
                            className={`p-1.5 rounded-lg border transition ${
                              isCurrent
                                ? 'opacity-30 cursor-not-allowed border-transparent text-slate-400'
                                : u.active
                                ? 'border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-950/30 text-amber-600 dark:text-amber-400 hover:bg-amber-100 cursor-pointer'
                                : 'border-emerald-200 dark:border-emerald-900/50 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-100 cursor-pointer'
                            }`}
                            title={
                              isCurrent
                                ? 'Saját fiókját nem tilthatja le'
                                : u.active
                                ? 'Fiók azonnali letiltása / felfüggesztése'
                                : 'Fiók aktiválása / engedélyezése'
                            }
                          >
                            {u.active ? <Ban className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                          </button>

                          {/* Edit User */}
                          <button
                            onClick={() => openEditModal(u)}
                            className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-indigo-50 dark:hover:bg-slate-700 hover:text-indigo-600 transition cursor-pointer"
                            title="Felhasználó szerkesztése"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>

                          {/* Reset 2FA (Emergency unlock / lost phone) */}
                          {Boolean(u.totp_enabled) && (
                            <button
                              onClick={() => handleAdminReset2FA(u)}
                              className="p-1.5 rounded-lg border border-purple-200 dark:border-purple-900/50 bg-purple-50 dark:bg-purple-950/30 text-purple-600 dark:text-purple-400 hover:bg-purple-100 dark:hover:bg-purple-900/50 transition cursor-pointer"
                              title="2FA Feloldása / Kikapcsolása (pl. elhagyott telefon miatt)"
                            >
                              <ShieldOff className="w-4 h-4" />
                            </button>
                          )}

                          {/* Delete User */}
                          <button
                            onClick={() => setDeletingUser(u)}
                            disabled={isCurrent}
                            className={`p-1.5 rounded-lg border transition ${
                              isCurrent
                                ? 'opacity-30 cursor-not-allowed border-transparent text-slate-400'
                                : 'border-rose-200 dark:border-rose-900/40 bg-rose-50 dark:bg-rose-950/30 text-rose-600 dark:text-rose-400 hover:bg-rose-100 cursor-pointer'
                            }`}
                            title={isCurrent ? 'Saját fiók nem törölhető' : 'Felhasználó törlése'}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* CREATE USER MODAL */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-xs animate-in fade-in duration-150 overflow-y-auto">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-lg p-5 sm:p-6 shadow-2xl my-auto max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between pb-3 border-b border-slate-200 dark:border-slate-800 mb-4 shrink-0">
              <h2 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <UserCog className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                <span>Új Felhasználó Létrehozása</span>
              </h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {actionMsg && (
              <div className="mb-4 p-3 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-300 text-xs flex items-center gap-2 shrink-0">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{actionMsg.text}</span>
              </div>
            )}

            <form onSubmit={handleCreateUser} className="space-y-3.5 overflow-y-auto pr-1 flex-1">
              {/* Name */}
              <div>
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Név *</label>
                <input
                  type="text"
                  required
                  value={createForm.name}
                  onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                  placeholder="Minta Péter"
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Elsődleges E-mail Cím (Belépéshez) *</label>
                <input
                  type="email"
                  required
                  value={createForm.email}
                  onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                  placeholder="peter@onkormanyzat.hu"
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              {/* Notification Email */}
              <div>
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Értesítési E-mail Cím (Másodlagos / Reset)</label>
                <input
                  type="email"
                  value={createForm.notification_email}
                  onChange={(e) => setCreateForm({ ...createForm, notification_email: e.target.value })}
                  placeholder="peter.ertesites@privat.hu (opcionális)"
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              {/* Password */}
              <div>
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Kezdő Jelszó (min. 8 karakter) *</label>
                <div className="relative">
                  <input
                    type={showCreatePassword ? 'text' : 'password'}
                    required
                    value={createForm.password}
                    onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                    placeholder="Legalább 8 karakter..."
                    className="w-full pl-3 pr-10 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCreatePassword(!showCreatePassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 cursor-pointer"
                    title={showCreatePassword ? 'Jelszó elrejtése' : 'Jelszó megjelenítése'}
                  >
                    {showCreatePassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Tenant picker (Superadmin only) */}
              {isSuper && (
                <div>
                  <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Hozzárendelt Szervezet *</label>
                  <select
                    value={createForm.tenant_id}
                    onChange={(e) => setCreateForm({ ...createForm, tenant_id: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    {tenants.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name} ({t.slug})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Role */}
              <div>
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Szerepkör (RBAC Jogosultság) *</label>
                <select
                  value={createForm.role}
                  onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="operator">Ügyintéző (Gyors varázsló, adatbevitel)</option>
                  <option value="editor">Szerkesztő (Adatlapok, ZIP importálás)</option>
                  <option value="org_admin">Szervezeti Adminisztrátor (Intézményi jogok)</option>
                  <option value="auditor">Auditor (Csak olvasás, audit jelentések)</option>
                  {isSuper && (
                    <option value="superadmin">Superadmin (Teljes platformjog)</option>
                  )}
                </select>
              </div>

              {/* Login Demo Account Toggle */}
              <div className="p-3 bg-amber-50/60 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800/50 rounded-xl">
                <label className="flex items-center justify-between cursor-pointer">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
                    <div>
                      <div className="text-xs font-semibold text-slate-900 dark:text-white">
                        Megjelenítés a belépési oldalon
                      </div>
                      <div className="text-[11px] text-slate-500 dark:text-slate-400">
                        1-kattintásos belépési kártyaként jelenik meg – kizárólag a szervezet saját, egyedi belépési oldalán (Szervezetek → Belépési link). A rendszerszervezet fiókjai a központi oldalon.
                      </div>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={createForm.show_in_demo === 1}
                    onChange={(e) => setCreateForm({ ...createForm, show_in_demo: e.target.checked ? 1 : 0 })}
                    className="w-4 h-4 rounded text-amber-600 focus:ring-amber-500 border-slate-300 dark:border-slate-700 cursor-pointer"
                  />
                </label>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 text-slate-700 dark:text-slate-300 rounded-xl text-sm transition"
                >
                  Mégse
                </button>
                <button
                  type="submit"
                  disabled={actionLoading}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-sm font-medium transition shadow-sm flex items-center gap-2"
                >
                  {actionLoading && <RefreshCw className="w-4 h-4 animate-spin" />}
                  <span>Felhasználó Létrehozása</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* EDIT USER MODAL */}
      {showEditModal && editingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-xs animate-in fade-in duration-150 overflow-y-auto">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-lg p-5 sm:p-6 shadow-2xl my-auto max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between pb-3 border-b border-slate-200 dark:border-slate-800 mb-4 shrink-0">
              <h2 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <Edit2 className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                <span>Felhasználó Módosítása (#{editingUser.id})</span>
              </h2>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {actionMsg && (
              <div className="mb-4 p-3 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-300 text-xs flex items-center gap-2 shrink-0">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{actionMsg.text}</span>
              </div>
            )}

            <form onSubmit={handleUpdateUser} className="space-y-3.5 overflow-y-auto pr-1 flex-1">
              {/* Name */}
              <div>
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Név *</label>
                <input
                  type="text"
                  required
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Elsődleges E-mail Cím *</label>
                <input
                  type="email"
                  required
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              {/* Notification Email */}
              <div>
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Értesítési E-mail Cím</label>
                <input
                  type="email"
                  value={editForm.notification_email}
                  onChange={(e) => setEditForm({ ...editForm, notification_email: e.target.value })}
                  placeholder="Értesítési cím (opcionális)"
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              {/* Tenant selector (Superadmin only) */}
              {isSuper && (
                <div>
                  <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Szervezet Áthelyezése</label>
                  <select
                    value={editForm.tenant_id}
                    onChange={(e) => setEditForm({ ...editForm, tenant_id: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    {tenants.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Role */}
              <div>
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Szerepkör</label>
                <select
                  value={editForm.role}
                  onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="operator">Ügyintéző</option>
                  <option value="editor">Szerkesztő</option>
                  <option value="org_admin">Szervezeti Adminisztrátor</option>
                  <option value="auditor">Auditor (Csak olvasás)</option>
                  {isSuper && <option value="superadmin">Superadmin</option>}
                </select>
              </div>

              {/* Active Toggle */}
              <div>
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Fiók Státusz</label>
                <select
                  value={editForm.active}
                  onChange={(e) => setEditForm({ ...editForm, active: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value={1}>Aktív (Bejelentkezés engedélyezve)</option>
                  <option value={0}>Letiltva / Felfüggesztve (Bejelentkezés megtiltva)</option>
                </select>
              </div>

              {/* Login Demo Account Toggle */}
              <div className="p-3 bg-amber-50/60 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800/50 rounded-xl">
                <label className="flex items-center justify-between cursor-pointer">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
                    <div>
                      <div className="text-xs font-semibold text-slate-900 dark:text-white">
                        Megjelenítés a belépési oldalon
                      </div>
                      <div className="text-[11px] text-slate-500 dark:text-slate-400">
                        1-kattintásos belépési kártyaként jelenik meg – kizárólag a szervezet saját, egyedi belépési oldalán (Szervezetek → Belépési link). A rendszerszervezet fiókjai a központi oldalon.
                      </div>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={editForm.show_in_demo === 1}
                    onChange={(e) => setEditForm({ ...editForm, show_in_demo: e.target.checked ? 1 : 0 })}
                    className="w-4 h-4 rounded text-amber-600 focus:ring-amber-500 border-slate-300 dark:border-slate-700 cursor-pointer"
                  />
                </label>
              </div>

              {/* 2FA Status & Admin Unlock */}
              {Boolean(editingUser.totp_enabled) && (
                <div className="p-3 bg-purple-50/60 dark:bg-purple-950/20 border border-purple-200 dark:border-purple-800/60 rounded-xl flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-purple-600 dark:text-purple-400 shrink-0" />
                    <div>
                      <div className="text-xs font-semibold text-purple-900 dark:text-purple-200">2FA Védelem Aktív</div>
                      <div className="text-[10px] text-purple-700 dark:text-purple-300">A fiók jelenleg Authenticatorral védett</div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      handleAdminReset2FA(editingUser);
                      setShowEditModal(false);
                    }}
                    className="px-2.5 py-1 bg-purple-600 hover:bg-purple-500 text-white rounded-lg text-xs font-medium transition cursor-pointer flex items-center gap-1 shadow-xs"
                    title="2FA azonnali kikapcsolása / feloldása adminisztrátorként"
                  >
                    <ShieldOff className="w-3.5 h-3.5" />
                    <span>2FA Törlése</span>
                  </button>
                </div>
              )}

              {/* Reset Password Field */}
              <div className="pt-2 border-t border-slate-200 dark:border-slate-800">
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Új Jelszó Beállítása (opcionális)
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type={showEditPassword ? 'text' : 'password'}
                    value={editForm.password}
                    onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                    placeholder="Csak ha módosítani kívánja a jelszót..."
                    className="w-full pl-9 pr-10 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowEditPassword(!showEditPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 cursor-pointer"
                    title={showEditPassword ? 'Jelszó elrejtése' : 'Jelszó megjelenítése'}
                  >
                    {showEditPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 text-slate-700 dark:text-slate-300 rounded-xl text-sm transition"
                >
                  Mégse
                </button>
                <button
                  type="submit"
                  disabled={actionLoading}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-sm font-medium transition shadow-sm flex items-center gap-2"
                >
                  {actionLoading && <RefreshCw className="w-4 h-4 animate-spin" />}
                  <span>Módosítások Mentése</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* DELETE CONFIRMATION DIALOG */}
      {deletingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-xs animate-in fade-in duration-150 overflow-y-auto">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-md p-5 sm:p-6 shadow-2xl my-auto space-y-4">
            <div className="w-12 h-12 rounded-xl bg-rose-500/10 text-rose-600 dark:text-rose-400 flex items-center justify-center mx-auto border border-rose-500/20">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <div className="text-center">
              <h3 className="text-base font-bold text-slate-900 dark:text-white">Felhasználó Törlésének Megerősítése</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Biztosan törölni kívánja a(z) <span className="font-semibold text-slate-800 dark:text-slate-200">{deletingUser.name}</span> ({deletingUser.email}) felhasználói fiókot?
              </p>
              <p className="text-[11px] text-rose-600 dark:text-rose-400 mt-2 bg-rose-50 dark:bg-rose-950/30 p-2 rounded-lg border border-rose-200 dark:border-rose-900/40">
                A felhasználó összes aktív munkamenete azonnal megszűnik.
              </p>
            </div>
            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setDeletingUser(null)}
                className="px-4 py-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 text-slate-700 dark:text-slate-300 rounded-xl text-sm transition"
              >
                Mégse
              </button>
              <button
                type="button"
                onClick={handleDeleteUser}
                disabled={actionLoading}
                className="px-4 py-2 bg-rose-600 hover:bg-rose-500 text-white rounded-xl text-sm font-medium transition shadow-sm flex items-center gap-2"
              >
                {actionLoading && <RefreshCw className="w-4 h-4 animate-spin" />}
                <span>Igen, Végleges Törlés</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
